import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicReports, FrontendReport } from '@/services/reports.api';

interface ReportCardProps {
  report: FrontendReport;
  isZh: boolean;
  index: number;
}

const TYPE_META: Record<'report' | 'whitepaper' | 'brief', { zhLabel: string; enLabel: string; cls: string; icon: string }> = {
  report: { zhLabel: '年度报告', enLabel: 'Annual Report', cls: 'bg-[#0A2540]/10 text-[#0A2540]', icon: 'ri-bar-chart-box-line' },
  whitepaper: { zhLabel: '白皮书', enLabel: 'White Paper', cls: 'bg-[#2C7DA0]/12 text-[#2C7DA0]', icon: 'ri-file-text-line' },
  brief: { zhLabel: '政策简报', enLabel: 'Policy Brief', cls: 'bg-emerald-100 text-emerald-700', icon: 'ri-file-list-3-line' },
};

function ReportCard({ report, isZh, index }: ReportCardProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const meta = TYPE_META[report.type];

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setVisible(true); },
        { threshold: 0.1 }
      );
      if (ref.current) observer.observe(ref.current);
      return () => observer.disconnect();
    }, index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  const handleDownload = () => {
    if (report.downloadUrl && report.downloadUrl !== '#') {
      window.open(report.downloadUrl, '_blank');
    } else {
      // Simulate download
      const link = document.createElement('a');
      link.href = '#';
      link.setAttribute('download', `GTRA_${report.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div
      ref={ref}
      className={`group bg-white rounded-2xl overflow-hidden border border-[#0A2540]/8 hover:-translate-y-1 transition-all duration-300 flex flex-col ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Cover */}
      <div className="relative w-full h-44 overflow-hidden bg-[#F0F4F8]">
        <img
          src={report.cover}
          alt={isZh ? report.title : report.titleEn}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/50 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm bg-white/90 ${meta.cls}`}>
            <i className={`${meta.icon} text-[10px]`} />
            {isZh ? meta.zhLabel : meta.enLabel}
          </span>
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/90 text-[#0A2540] text-[10px] font-bold">
          {report.year}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-bold text-[#0A2540] text-sm leading-snug mb-2">
          {isZh ? report.title : report.titleEn}
        </h3>
        <p className="text-[#0A2540]/55 text-xs leading-relaxed mb-4 flex-1 line-clamp-3">
          {isZh ? report.description : report.descriptionEn}
        </p>

        {/* Meta row */}
        <div className="flex items-center gap-3 text-[#0A2540]/40 text-xs mb-4">
          <div className="flex items-center gap-1">
            <i className="ri-file-pdf-line" />
            <span>{isZh ? `${report.pages}页` : `${report.pages}pp`}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-download-cloud-line" />
            <span>{report.fileSize}</span>
          </div>
        </div>

        <button
          onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#0A2540]/15 text-[#0A2540] text-xs font-semibold hover:bg-[#0A2540] hover:text-white hover:border-[#0A2540] transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-download-2-line" />
          </div>
          {isZh ? '免费下载' : 'Free Download'}
        </button>
      </div>
    </div>
  );
}

export default function ReportsSection() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [sectionVisible, setSectionVisible] = useState(false);
  const [reports, setReports] = useState<FrontendReport[]>([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setSectionVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await getPublicReports();
        setReports(response || []);
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-[#F7F9FC]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-5 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-book-2-line" />
            </div>
            {t('aboutPage.reports.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4 leading-tight">
            {t('aboutPage.reports.title')}
          </h2>
          <p className="text-[#0A2540]/55 text-base max-w-2xl mx-auto leading-relaxed">
            {t('aboutPage.reports.subtitle')}
          </p>
        </div>

        {/* Reports Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[#0A2540]/50">{t('common.loading')}</div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[#0A2540]/50">{isZh ? '暂无报告' : 'No reports available'}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report, i) => (
              <ReportCard key={report.id} report={report} isZh={isZh} index={i} />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-500 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-[#0A2540]/5 border border-[#0A2540]/10 text-[#0A2540]/50 text-xs">
            <div className="w-4 h-4 flex items-center justify-center text-[#2C7DA0]">
              <i className="ri-shield-check-line" />
            </div>
            {isZh
              ? '所有文件均为公开版本，免费下载，无需注册'
              : 'All publications are publicly available, free to download, no registration required'}
          </div>
        </div>
      </div>
    </section>
  );
}
